import { useEffect, useRef, useState, type JSX } from "react";
import { Button, Dialog } from "../../../components";
import type { ReviewStepCurrent } from "../../../data/chatQuery";
import { useReviewSourceExcerpt } from "../../../data/reviewSourceExcerptQuery";
import { PrecedentDetail } from "../../PrecedentDetail";
import { TemplateDetail } from "../../TemplateDetail";
import { ClauseDiffLine } from "./ClauseDiffLine";
import { DraftWithDeviation } from "./reviewText";

const RISK_LABEL = { high: "High risk", medium: "Medium risk", low: "Low risk" } as const;

// The source open in the side preview (null = none). `clause` locates the cited
// clause inside it (template: standardClauseIndex; precedent: comparable clauseId).
type Preview = {
  kind: "template" | "precedent";
  id: string;
  label: string;
  clause?: string | number | null;
};

const DATE_LABEL = {
  certified: "certified",
  effective: "effective",
  executed: "executed",
  uploaded: "uploaded",
} as const;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// The preview pane body: a short excerpt of the cited template / precedent with
// the matching clause highlighted. If the excerpt can't be had (not visible,
// tier, older record), fall back to the full record view so the pane never dead-ends.
function SourcePreview({ preview }: { preview: Preview }): JSX.Element {
  const q = useReviewSourceExcerpt({
    kind: preview.kind,
    id: preview.id,
    clause: preview.clause ?? null,
  });
  const matchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    matchRef.current?.scrollIntoView({ block: "center" });
  }, [q.data]);

  if (q.isPending) {
    return (
      <div aria-busy="true" data-testid="review-source-loading">
        <div className="sa-why__loading">Loading…</div>
        <div className="sa-why__skel" />
        <div className="sa-why__skel is-short" />
        <div className="sa-why__skel" />
      </div>
    );
  }
  if (q.isError || q.data.clauses.length === 0) {
    return preview.kind === "template" ? (
      <TemplateDetail templateId={preview.id} embedded />
    ) : (
      <PrecedentDetail id={preview.id} embedded />
    );
  }
  const { meta, clauses, matchIndex } = q.data;
  const bits = [
    meta.parties.length > 0 ? meta.parties.join(" / ") : null,
    [meta.guild, meta.dealType].filter(Boolean).join(" · ") || null,
    meta.date !== null && meta.dateLabel !== null
      ? `${DATE_LABEL[meta.dateLabel]} ${fmtDate(meta.date)}`
      : null,
  ].filter((x): x is string => x !== null);
  return (
    <div data-testid="review-source-excerpt">
      {bits.length > 0 && <div className="sa-why__meta-line">{bits.join(" · ")}</div>}
      <div className="sa-why__excerpt">
        {clauses.map((c, i) => (
          <div
            key={i}
            ref={i === matchIndex ? matchRef : undefined}
            className={`sa-why__ex-clause${
              i === matchIndex ? ` is-match is-${preview.kind === "template" ? "l1" : "l2"}` : ""
            }`}
            data-match={i === matchIndex ? "true" : undefined}
          >
            {c.label.length > 0 && <b>{c.label} </b>}
            {c.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function WarnIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M6 1.5l5 9H1l5-9z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6 5v2.5M6 9v.01" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

// "250 of your precedents include…" / "3 comparable past deals found" → "250".
function leadingCount(summary: string): string | null {
  const m = /^(\d+)\b/.exec(summary.trim());
  return m ? (m[1] ?? null) : null;
}

// "Why this recommendation" — a READ-ONLY dialog over the open clause card: the
// explanation, the draft-vs-certified wording, and one line per source layer
// (certified template L1, precedents L2, conflicts). Clicking a source opens it
// in a preview pane beside the list (a Back view on mobile). It never decides:
// Approve/Keep/Type/Skip stay on the card, and closing returns to the chat.
export function ReviewWhyDialog({
  cur,
  open,
  onOpenChange,
  onJumpToDraft,
  revising,
}: {
  cur: ReviewStepCurrent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJumpToDraft?: () => void;
  revising: boolean;
}): JSX.Element {
  const [preview, setPreview] = useState<Preview | null>(null);
  // Always reopen without a stale preview.
  useEffect(() => {
    if (!open) setPreview(null);
  }, [open]);

  const reco = cur.recommendation;
  const precedent = reco?.precedent ?? null;
  const isActive = (kind: Preview["kind"], id: string): boolean =>
    preview !== null && preview.kind === kind && preview.id === id;
  const count = precedent !== null ? leadingCount(precedent.summary) : null;

  const openPreview = (p: Preview): void => setPreview(p);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="sa-why__scrim"
      contentClassName={`sa-why${preview !== null ? " has-preview" : ""}`}
      titleClassName="sa-why__sr"
      descriptionClassName="sa-why__sr"
      description="The explanation, wording and sources behind this recommendation"
      // Esc closes the preview first; a second Esc closes the dialog.
      onEscapeKeyDown={(e) => {
        if (preview !== null) {
          e.preventDefault();
          setPreview(null);
        }
      }}
      title={`Why this recommendation — ${cur.name}`}
    >
      <div
        className={`sa-why__list${preview !== null ? " is-covered" : ""}`}
        data-testid="review-why-dialog"
      >
        <div className="sa-why__head">
          <span className="sa-why__no">
            {cur.displayNumber != null && cur.displayNumber.length > 0
              ? `§ ${cur.displayNumber}`
              : String(cur.index + 1).padStart(2, "0")}
          </span>
          <span className="sa-why__name" data-testid="review-why-title">{cur.name}</span>
          <span className={`sa-why__risk is-${cur.risk}`}>
            <i aria-hidden="true" />
            {RISK_LABEL[cur.risk]}
          </span>
          {cur.category === "additional" && (
            <span className="sa-why__meta">· Additional clause</span>
          )}
          {cur.category === "missing" && (
            <span className="sa-why__meta">· Missing from the draft</span>
          )}
          <Button
            variant="bare"
            className="sa-why__x"
            aria-label="Close"
            data-testid="review-why-close"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon />
          </Button>
        </div>

        <div className="sa-why__body">
          {revising && (
            <div className="sa-rev__revising" data-testid="review-revising">
              Revising the recommendation…
            </div>
          )}

          <section className="sa-why__sec">
            <h3 className="sa-why__label">Recommended change</h3>
            <p className="sa-why__p sa-why__p--lead">
              {reco !== null
                ? reco.whyPlain != null && reco.whyPlain.length > 0
                  ? reco.whyPlain
                  : reco.why
                : "Additional clause — the certified standard doesn’t cover this."}
            </p>
            {reco?.coherenceNote != null && reco.coherenceNote.length > 0 && (
              <p className="sa-why__heads">
                <WarnIcon />
                {reco.coherenceNote}
              </p>
            )}
            {reco?.take != null && reco.take.length > 0 && (
              <p className="sa-why__take">{reco.take}</p>
            )}
          </section>

          <section className="sa-why__sec">
            <h3 className="sa-why__label">Where it changes</h3>
            <div className="sa-why__diff" data-testid="review-why-diff">
              <ClauseDiffLine cur={cur} />
            </div>
            {reco !== null && (
              <div className="sa-why__legend">
                <span className="sa-cr__del">red</span> comes out of your draft ·{" "}
                <span className="sa-cr__ins">green</span> goes in from the certified language
              </div>
            )}
          </section>

          <section>
            <div className="sa-why__sec-head">
              <h3 className="sa-why__label">Before and after</h3>
              {onJumpToDraft && (
                <Button
                  variant="bare"
                  className="sa-why__jump"
                  onClick={() => {
                    onOpenChange(false);
                    onJumpToDraft();
                  }}
                >
                  Show in draft ↗
                </Button>
              )}
            </div>
            <div className="sa-why__blocks">
              <div className="sa-why__block">
                <div className="sa-why__block-label">Before · in your draft</div>
                <div className="sa-why__doc">
                  <DraftWithDeviation text={cur.draftText} span={cur.deviationSpan} />
                </div>
              </div>
              {reco !== null && (
                <div className="sa-why__block is-cert">
                  <div className="sa-why__block-label">After · certified language</div>
                  <div className="sa-why__doc" data-testid="review-recommendation-text">
                    {reco.text}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section data-testid="review-why-sources">
            <h3 className="sa-why__label">Sources</h3>
            <div className="sa-why__srcs">
              {reco !== null && (
                <div className="sa-why__line">
                  <span className="sa-why__tag is-l1">L1</span>
                  {reco.templateId != null ? (
                    <Button
                      variant="bare"
                      className={`sa-why__link is-l1${
                        isActive("template", reco.templateId) ? " is-active" : ""
                      }`}
                      data-testid="review-source-template"
                      onClick={() =>
                        openPreview({
                          kind: "template",
                          id: reco.templateId as string,
                          label: reco.sourceLabel,
                          clause: reco.standardClauseIndex ?? null,
                        })
                      }
                    >
                      {reco.sourceLabel}
                    </Button>
                  ) : (
                    <span className="sa-why__plain">{reco.sourceLabel}</span>
                  )}
                </div>
              )}

              {precedent !== null && (
                <div className="sa-why__line">
                  <span className="sa-why__tag is-l2">L2</span>
                  <span className="sa-why__links">
                    <span className="sa-why__count">
                      {count !== null ? `${count} precedents —` : precedent.summary}
                    </span>
                    {precedent.comparables.map((c, i) =>
                      c.precedentId != null ? (
                        <Button
                          key={`${c.precedentId}-${i}`}
                          variant="bare"
                          title={c.title}
                          className={`sa-why__link is-l2${
                            isActive("precedent", c.precedentId) ? " is-active" : ""
                          }`}
                          data-testid="review-source-precedent"
                          onClick={() =>
                            openPreview({
                              kind: "precedent",
                              id: c.precedentId as string,
                              label: c.title,
                              clause: c.clauseId ?? null,
                            })
                          }
                        >
                          {c.title}
                        </Button>
                      ) : (
                        <span key={`${c.title}-${i}`} className="sa-why__plain" title={c.title}>
                          {c.title}
                        </span>
                      )
                    )}
                  </span>
                </div>
              )}

              {cur.conflicts.map((c, i) => (
                <div key={`${c.standardClauseName}-${i}`} className="sa-why__line">
                  <span className="sa-why__tag is-conflict">!</span>
                  <span className="sa-why__conflict">
                    <b>Conflicts with {c.standardClauseName}</b> — {c.reason}
                  </span>
                </div>
              ))}

              {reco === null && precedent === null && cur.conflicts.length === 0 && (
                <div className="sa-why__plain">No other sources for this clause.</div>
              )}
            </div>
          </section>
        </div>
      </div>

      {preview !== null && (
        <aside className="sa-why__preview" data-testid="review-source-detail">
          <div className="sa-why__pv-head">
            <Button
              variant="bare"
              className="sa-why__back"
              data-testid="review-source-back"
              onClick={() => setPreview(null)}
            >
              ‹ Back
            </Button>
            <span className={`sa-why__tag is-${preview.kind === "template" ? "l1" : "l2"}`}>
              {preview.kind === "template" ? "L1" : "L2"}
            </span>
            <span className="sa-why__pv-title">{preview.label}</span>
            <a
              className="sa-why__open"
              href={
                preview.kind === "template"
                  ? `/library/${encodeURIComponent(preview.id)}`
                  : `/precedent/${encodeURIComponent(preview.id)}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Open ↗
            </a>
            <Button
              variant="bare"
              className="sa-why__x sa-why__x--pv"
              aria-label="Close preview"
              onClick={() => setPreview(null)}
            >
              <CloseIcon />
            </Button>
          </div>
          <div className="sa-why__pv-body">
            <SourcePreview preview={preview} />
          </div>
        </aside>
      )}
    </Dialog>
  );
}
